import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  User,
  Mail,
  Calendar,
  CreditCard,
  Crown,
  AlertTriangle,
  Loader2,
  Shield,
  BookOpen,
  FileText,
  LogOut,
  Trash2,
  Sparkles,
  ImageIcon,
  Pencil,
  Check,
  X,
} from 'lucide-react';
import { useAuthStore } from '../stores/auth-store';
import { useSubscriptionStore, PLAN_NAMES } from '../stores/subscription-store';
import { Button } from './ui/button';
import { AddonsPricing } from './addons-pricing';
import { AvatarUpload } from './avatar-upload';
import { cn } from '../lib/utils';
import { toast } from '../lib/toast';
import { api } from '../lib/api';

type SectionId = 'profile' | 'subscription' | 'security' | 'account';

interface AiUsageData {
  text: { used: number; limit: number };
  cover: { used: number; limit: number };
}

/* Status badge — only semantic tokens; no raw hex. Trialing maps to primary
   (moonlit indigo), active to a green-adjacent muted, etc. */
const STATUS_BADGE: Record<string, { bg: string; text: string; labelKey: string; labelFallback: string }> = {
  trialing: {
    bg: 'bg-primary/10',
    text: 'text-primary',
    labelKey: 'accountView.statusTrial',
    labelFallback: 'Trial',
  },
  active: {
    bg: 'bg-secondary',
    text: 'text-foreground',
    labelKey: 'accountView.statusActive',
    labelFallback: 'Ativo',
  },
  past_due: {
    bg: 'bg-destructive/10',
    text: 'text-destructive',
    labelKey: 'accountView.statusPastDue',
    labelFallback: 'Pagamento Pendente',
  },
  canceled: {
    bg: 'bg-destructive/10',
    text: 'text-destructive',
    labelKey: 'accountView.statusCanceled',
    labelFallback: 'Cancelado',
  },
  expired: {
    bg: 'bg-muted',
    text: 'text-muted-foreground',
    labelKey: 'accountView.statusExpired',
    labelFallback: 'Expirado',
  },
};

export function AccountView() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, logout, updateProfile } = useAuthStore();
  const {
    plan,
    status,
    daysRemaining,
    usage,
    trialEndsAt,
    currentPeriodEnd,
    cancelAtPeriodEnd,
    fetchSubscription,
    fetchUsage,
  } = useSubscriptionStore();
  const [isLoading, setIsLoading] = useState(true);
  const [section, setSection] = useState<SectionId>('profile');
  const [searchParams] = useSearchParams();

  // Allow deep-linking to a tab, e.g. /app/account?tab=subscription (from the
  // AI usage indicator's "Ver planos e uso completo").
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'profile' || tab === 'subscription' || tab === 'security' || tab === 'account') {
      setSection(tab);
    }
  }, [searchParams]);
  const [aiUsage, setAiUsage] = useState<AiUsageData | null>(null);

  // Name editing state
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState('');
  const [savingName, setSavingName] = useState(false);
  const nameInputRef = useRef<HTMLInputElement>(null);

  // Email verification state
  const [sendingVerification, setSendingVerification] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      await Promise.all([
        fetchSubscription(),
        fetchUsage(),
        api.getAiUsage().then(setAiUsage).catch(() => null),
      ]);
      setIsLoading(false);
    };
    loadData();
  }, [fetchSubscription, fetchUsage]);

  const handleStartEditName = () => {
    setNameValue(user?.name ?? '');
    setEditingName(true);
    // Focus input after render
    setTimeout(() => nameInputRef.current?.focus(), 50);
  };

  const handleCancelEditName = () => {
    setEditingName(false);
    setNameValue('');
  };

  const handleSaveName = async () => {
    const trimmed = nameValue.trim();
    if (!trimmed) {
      toast.error(t('account.nameRequired', 'O nome não pode estar vazio.'));
      return;
    }
    if (trimmed === user?.name) {
      setEditingName(false);
      return;
    }
    setSavingName(true);
    try {
      const ok = await updateProfile({ name: trimmed });
      if (ok) {
        toast.success(t('account.nameSaved', 'Nome atualizado com sucesso.'));
        setEditingName(false);
      } else {
        toast.error(t('account.nameSaveError', 'Falha ao salvar o nome.'));
      }
    } finally {
      setSavingName(false);
    }
  };

  const handleRequestEmailVerification = async () => {
    setSendingVerification(true);
    try {
      await api.requestEmailVerification();
      toast.success(
        t('account.verificationSent', 'E-mail de verificação enviado! Verifique sua caixa de entrada.')
      );
    } catch {
      toast.error(t('account.verificationError', 'Falha ao enviar e-mail de verificação.'));
    } finally {
      setSendingVerification(false);
    }
  };

  const handleLogout = async () => {
    toast(t('account.logoutConfirm', 'Sign out of your account?'), {
      action: {
        label: t('common.confirm'),
        onClick: async () => {
          await logout();
          navigate('/');
        },
      },
      cancel: { label: t('common.cancel'), onClick: () => {} },
      duration: 8000,
    });
  };

  const formatDate = (date: Date | string | null | undefined) => {
    if (!date) return '-';
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    }).format(new Date(date));
  };

  const statusBadge = STATUS_BADGE[status] ?? STATUS_BADGE.expired;

  const sections: { id: SectionId; label: string; icon: React.ElementType }[] = [
    { id: 'profile', label: t('account.profile', 'Perfil'), icon: User },
    { id: 'subscription', label: t('account.subscription', 'Assinatura'), icon: CreditCard },
    { id: 'security', label: t('account.security', 'Segurança'), icon: Shield },
    { id: 'account', label: t('account.accountSection', 'Conta'), icon: AlertTriangle },
  ];

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center min-h-[320px]">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-7 w-7 animate-spin text-primary/60" />
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            {t('common.loading', 'Carregando...')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto [-webkit-overflow-scrolling:touch]">
      <div className="mx-auto w-full max-w-7xl px-4 py-6 md:px-10 md:py-10">

        {/* Page header — mock "scr-conta": mono eyebrow + serif headline */}
        <div className="mb-6 border-b border-border pb-5 md:mb-8 md:pb-6">
          <p className="font-mono text-[8.5px] uppercase tracking-[0.3em] text-muted-foreground">
            {t('account.title', 'Minha Conta')}
          </p>
          <h1 className="mt-1.5 font-serif text-[22px] font-medium leading-tight text-foreground">
            {t('account.subtitle', 'Gerencie seu perfil, assinatura e segurança')}
          </h1>
        </div>

        {/* Account summary card — mock ".acct-card": avatar + name + plan */}
        <div className="mb-6 flex items-center gap-4 rounded-2xl border border-border bg-card p-5">
          <AvatarUpload size={58} />
          <div className="min-w-0 flex-1">
            <p className="truncate font-serif text-[17px] font-medium text-foreground">
              {user?.name ?? t('accountView.defaultUserName', 'Usuário')}
            </p>
            <p className="mt-1 truncate font-mono text-[8.5px] uppercase tracking-[0.3em] text-muted-foreground">
              {PLAN_NAMES[plan]}
            </p>
          </div>
        </div>

        {/* Trial Banner */}
        {status === 'trialing' && daysRemaining > 0 && (
          <div className="mb-6 bg-primary/10 border border-primary/20 rounded-xl p-4 flex items-start gap-3">
            <Crown className="h-5 w-5 text-primary shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="font-medium text-foreground">
                {t('subscription.trialBanner', 'Você está em um período de teste de {{days}} dias do Plano Professional', {
                  days: daysRemaining,
                })}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                {t('account.trialEndsOn', 'Seu período de teste encerra em {{date}}', {
                  date: formatDate(trialEndsAt),
                })}
              </p>
            </div>
            <Button size="sm" onClick={() => navigate('/pricing')} className="shrink-0">
              {t('subscription.upgradeNow', 'Fazer Upgrade')}
            </Button>
          </div>
        )}

        {/* Past Due Banner */}
        {status === 'past_due' && (
          <div className="mb-6 bg-destructive/[0.07] border border-destructive/20 rounded-xl p-4 flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="font-medium text-foreground">
                {t('account.pastDue', 'Pagamento falhou')}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                {t('account.updatePayment', 'Atualize seu método de pagamento para continuar.')}
              </p>
            </div>
            <Button size="sm" variant="outline" className="shrink-0 border-destructive/30 text-destructive hover:bg-destructive/10">
              {t('account.updatePaymentMethod', 'Atualizar Pagamento')}
            </Button>
          </div>
        )}

        <div className="md:grid md:grid-cols-[220px_minmax(0,1fr)] md:gap-10 md:items-start">

          {/* Section nav — sticky sidebar on desktop, horizontal scroll on mobile */}
          <nav
            className={cn(
              'mb-6 flex gap-1.5 overflow-x-auto no-scrollbar pb-1 -mx-4 px-4',
              'md:mx-0 md:mb-0 md:flex-col md:gap-1 md:overflow-visible md:px-0 md:pb-0',
              'md:sticky md:top-6 md:self-start'
            )}
          >
            {sections.map(({ id, label, icon: Icon }) => {
              const active = section === id;
              return (
                <button
                  key={id}
                  onClick={() => setSection(id)}
                  className={cn(
                    'flex shrink-0 items-center gap-2.5 whitespace-nowrap rounded-xl px-3 py-2.5 text-xs font-medium',
                    'transition-all duration-150',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60 focus-visible:ring-offset-1',
                    active
                      ? 'bg-primary/10 text-primary'
                      : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground active:bg-muted/80',
                    'md:w-full'
                  )}
                >
                  <span
                    className={cn(
                      'flex h-6 w-6 shrink-0 items-center justify-center rounded-lg transition-colors duration-150',
                      active
                        ? 'bg-primary/15 text-primary'
                        : 'bg-muted text-muted-foreground'
                    )}
                  >
                    <Icon size={13} />
                  </span>
                  <span className="font-mono text-[11px] uppercase tracking-wide">{label}</span>
                </button>
              );
            })}
          </nav>

          {/* Active section content */}
          <div key={section} className="min-w-0 space-y-4 animate-fade-in md:min-h-[480px]">

            {/* ── PROFILE ── */}
            {section === 'profile' && (
              <AccountPanel label={t('account.profile', 'Perfil')}>
                <div className="space-y-4">

                  {/* Avatar (click to upload) + name */}
                  <div className="flex items-center gap-4">
                    <AvatarUpload size={56} />
                    <div className="min-w-0">
                      <p className="font-serif font-semibold text-lg text-foreground truncate">
                        {user?.name ?? t('accountView.defaultUserName', 'Usuário')}
                      </p>
                      <p className="text-sm text-muted-foreground flex items-center gap-1.5 mt-0.5">
                        <Mail size={13} className="shrink-0" />
                        <span className="truncate">{user?.email ?? 'email@example.com'}</span>
                      </p>
                    </div>
                  </div>

                  {/* Member since */}
                  <ProfileRow label={t('account.memberSince', 'Membro desde')}>
                    <div className="flex items-center gap-1.5 text-sm text-foreground">
                      <Calendar size={13} className="text-muted-foreground shrink-0" />
                      {formatDate(user?.createdAt)}
                    </div>
                  </ProfileRow>

                  {/* Name row — editable */}
                  <ProfileRow label={t('account.name', 'Nome')}>
                    {editingName ? (
                      <div className="flex items-center gap-2">
                        <input
                          ref={nameInputRef}
                          type="text"
                          value={nameValue}
                          onChange={(e) => setNameValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSaveName();
                            if (e.key === 'Escape') handleCancelEditName();
                          }}
                          disabled={savingName}
                          className={cn(
                            'flex-1 min-w-0 rounded-[11px] bg-card ring-1 ring-inset ring-border',
                            'px-3 py-1.5 text-sm text-foreground',
                            'outline-none transition-shadow duration-150',
                            'focus:ring-2 focus:ring-ring/40',
                            'disabled:opacity-50'
                          )}
                          placeholder={t('account.namePlaceholder', 'Seu nome')}
                        />
                        <Button
                          size="sm"
                          onClick={handleSaveName}
                          disabled={savingName}
                          className="shrink-0 gap-1 h-8 px-2.5 font-mono text-xs rounded-lg"
                        >
                          {savingName ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <Check className="w-3.5 h-3.5" />
                          )}
                          {t('common.save', 'Salvar')}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleCancelEditName}
                          disabled={savingName}
                          className="shrink-0 h-8 w-8 p-0 rounded-lg"
                        >
                          <X className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between gap-4">
                        <span className="text-sm text-foreground truncate">
                          {user?.name ?? '-'}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleStartEditName}
                          className="shrink-0 gap-1.5 font-mono text-xs rounded-lg"
                        >
                          <Pencil className="w-3 h-3" />
                          {t('account.change', 'Alterar')}
                        </Button>
                      </div>
                    )}
                  </ProfileRow>

                  {/* Email row */}
                  <ProfileRow label={t('account.email', 'Email')}>
                    <div className="flex items-center justify-between gap-4">
                      <span className="text-sm text-foreground truncate">
                        {user?.email ?? '-'}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled
                        className="shrink-0 font-mono text-xs rounded-lg"
                      >
                        {t('account.change', 'Alterar')}
                      </Button>
                    </div>
                  </ProfileRow>

                  {/* Email verification notice */}
                  {user && !(user as unknown as { emailVerified?: boolean }).emailVerified && (
                    <div className="flex items-start gap-3 rounded-xl border border-amber-500/25 bg-amber-500/5 p-3.5">
                      <AlertTriangle size={15} className="mt-0.5 shrink-0 text-amber-500" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-foreground">
                          {t('account.emailNotVerified', 'E-mail não verificado')}
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {t(
                            'account.emailNotVerifiedDesc',
                            'Verifique seu e-mail para garantir acesso completo à conta.'
                          )}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handleRequestEmailVerification}
                        disabled={sendingVerification}
                        className="shrink-0 font-mono text-xs rounded-lg border-amber-500/30 text-amber-600 hover:bg-amber-500/10 hover:border-amber-500/50"
                      >
                        {sendingVerification ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          t('account.resendVerification', 'Reenviar')
                        )}
                      </Button>
                    </div>
                  )}
                </div>
              </AccountPanel>
            )}

            {/* ── SUBSCRIPTION ── */}
            {section === 'subscription' && (
              <>
                {/* Plan overview + usage */}
                <AccountPanel label={t('account.subscription', 'Assinatura')}>
                  <div className="space-y-5">

                    {/* Plan name + status badge */}
                    <div className="flex items-center gap-2.5 flex-wrap">
                      <p className="font-serif font-semibold text-lg text-foreground">
                        {PLAN_NAMES[plan]}
                      </p>
                      <span
                        className={cn(
                          'px-2.5 py-0.5 rounded-full font-mono text-[10px] uppercase tracking-wider font-medium',
                          statusBadge.bg,
                          statusBadge.text
                        )}
                      >
                        {t(statusBadge.labelKey, statusBadge.labelFallback)}
                      </span>
                    </div>

                    {status === 'active' && currentPeriodEnd && (
                      <p className="text-sm text-muted-foreground -mt-3">
                        {cancelAtPeriodEnd
                          ? t('account.endsOn', 'Encerra em {{date}}', {
                              date: formatDate(currentPeriodEnd),
                            })
                          : t('account.renewsOn', 'Renova em {{date}}', {
                              date: formatDate(currentPeriodEnd),
                            })}
                      </p>
                    )}

                    {/* Plan usage bars */}
                    {usage && (
                      <div className="border-t border-border pt-4 space-y-4">
                        <FieldLabel>{t('account.usage', 'Uso')}</FieldLabel>

                        {/* Projects */}
                        <UsageBar
                          icon={<BookOpen size={12} />}
                          label={t('account.projects', 'Projetos')}
                          used={usage.projects.used}
                          limit={usage.projects.unlimited ? -1 : usage.projects.limit}
                          formatUsed={(n) => String(n)}
                          formatLimit={(n) => String(n)}
                        />

                        {/* Words */}
                        <UsageBar
                          icon={<FileText size={12} />}
                          label={t('account.wordsThisMonth', 'Palavras este mês')}
                          used={usage.words.used ?? 0}
                          limit={usage.words.unlimited ? -1 : usage.words.limit}
                          formatUsed={(n) => `${(n / 1000).toFixed(1)}k`}
                          formatLimit={(n) => `${(n / 1000).toFixed(0)}k`}
                        />
                      </div>
                    )}

                    {/* AI usage bars */}
                    {aiUsage && (
                      <div className="border-t border-border pt-4 space-y-4">
                        <FieldLabel>{t('account.aiUsage', 'Uso de IA este mês')}</FieldLabel>

                        {/* AI Text */}
                        <UsageBar
                          icon={<Sparkles size={12} />}
                          label={t('account.aiText', 'Ações de IA (texto)')}
                          used={aiUsage.text.used}
                          limit={aiUsage.text.limit}
                          formatUsed={(n) => String(n)}
                          formatLimit={(n) => String(n)}
                          colorful
                        />

                        {/* AI Cover */}
                        <UsageBar
                          icon={<ImageIcon size={12} />}
                          label={t('account.aiCover', 'Gerações de capa')}
                          used={aiUsage.cover.used}
                          limit={aiUsage.cover.limit}
                          formatUsed={(n) => String(n)}
                          formatLimit={(n) => String(n)}
                          colorful
                        />
                      </div>
                    )}
                  </div>
                </AccountPanel>

                {/* Add-ons */}
                <AccountPanel label={t('account.addons', 'Add-ons')}>
                  <AddonsPricing variant="app" forceSlider />
                </AccountPanel>
              </>
            )}

            {/* ── SECURITY ── */}
            {section === 'security' && (
              <AccountPanel label={t('account.security', 'Segurança')}>
                <div className="space-y-3">
                  <div className="rounded-[11px] bg-card ring-1 ring-inset ring-border px-4 py-3">
                    <div className="flex items-center justify-between gap-4">
                      <div className="min-w-0">
                        <FieldLabel>{t('account.password', 'Senha')}</FieldLabel>
                        <p className="text-sm text-muted-foreground">
                          {t('account.passwordDesc', 'Altere a senha da sua conta')}
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled
                        className="shrink-0 rounded-lg font-mono text-xs"
                      >
                        {t('account.changePassword', 'Alterar')}
                      </Button>
                    </div>
                  </div>

                  {/* Privacy notice */}
                  <div className="flex gap-2.5 rounded-xl border border-border bg-muted/20 p-3.5 text-muted-foreground">
                    <Shield size={15} className="mt-0.5 shrink-0 text-primary/60" />
                    <p className="text-xs leading-relaxed">
                      {t(
                        'account.securityNote',
                        'Sua conta é protegida por autenticação segura. Nunca compartilhe sua senha.'
                      )}
                    </p>
                  </div>
                </div>
              </AccountPanel>
            )}

            {/* ── ACCOUNT ── */}
            {section === 'account' && (
              <>
                {/* Logout */}
                <AccountPanel label={t('account.session', 'Sessão')}>
                  <div className="rounded-[11px] bg-card ring-1 ring-inset ring-border px-4 py-3">
                    <div className="flex items-center justify-between gap-4">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground">
                          {t('account.logout', 'Sair da conta')}
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {t('account.logoutDesc', 'Encerra a sessão atual neste dispositivo')}
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="shrink-0 gap-1.5 rounded-lg font-mono text-xs transition-colors"
                        onClick={handleLogout}
                      >
                        <LogOut size={13} />
                        {t('account.logout', 'Sair')}
                      </Button>
                    </div>
                  </div>
                </AccountPanel>

                {/* Danger zone — wrapped in AccountPanel for visual consistency */}
                <AccountPanel
                  label={t('account.dangerZone', 'Zona de Perigo')}
                  danger
                >
                  <div className="rounded-[11px] bg-card ring-1 ring-inset ring-border px-4 py-3">
                    <div className="flex items-center justify-between gap-4">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground">
                          {t('account.deleteAccount', 'Excluir conta')}
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {t(
                            'account.deleteDesc',
                            'Remove permanentemente sua conta e todos os dados'
                          )}
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled
                        className={cn(
                          'shrink-0 gap-1.5 rounded-lg border-destructive/30 font-mono text-xs text-destructive',
                          'hover:border-destructive/60 hover:bg-destructive/10 hover:text-destructive',
                          'transition-colors duration-150'
                        )}
                      >
                        <Trash2 size={13} />
                        {t('common.delete')}
                      </Button>
                    </div>
                  </div>
                </AccountPanel>
              </>
            )}

          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Sub-components ── */

/** Card grouper — mock ".dcard": rounded card, translucent bg, small mono label header. */
function AccountPanel({
  label,
  children,
  danger = false,
}: {
  label: string;
  children: React.ReactNode;
  danger?: boolean;
}) {
  return (
    <div
      className={cn(
        'rounded-2xl border p-5 md:p-6 space-y-4',
        danger
          ? 'border-destructive/25 bg-destructive/[0.04]'
          : 'border-border bg-card/60'
      )}
    >
      <h2
        className={cn(
          'font-mono text-[8.5px] font-semibold uppercase tracking-[0.26em]',
          danger ? 'text-destructive/80' : 'text-muted-foreground'
        )}
      >
        {label}
      </h2>
      {children}
    </div>
  );
}

/** Small field label — mock ".sh-l": mono, tiny, wide tracking, muted. */
function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <label className="mb-2 block font-mono text-[7.5px] uppercase tracking-[0.2em] text-muted-foreground">
      {children}
    </label>
  );
}

/** Reusable profile info row — mock ".fld": card bg, inset ring, rounded 11px. */
function ProfileRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-[11px] bg-card ring-1 ring-inset ring-border px-4 py-3 space-y-2">
      <FieldLabel>{label}</FieldLabel>
      {children}
    </div>
  );
}

/** Usage progress bar with smart color coding. */
function UsageBar({
  icon,
  label,
  used,
  limit,
  formatUsed,
  formatLimit,
  colorful = false,
}: {
  icon: React.ReactNode;
  label: string;
  used: number;
  limit: number;
  formatUsed: (n: number) => string;
  formatLimit: (n: number) => string;
  colorful?: boolean;
}) {
  const unlimited = limit === -1;
  const ratio = unlimited ? 0 : Math.min(1, used / limit);
  const pct = Math.round(ratio * 100);

  const barColor = !colorful
    ? 'bg-primary'
    : ratio >= 1
    ? 'bg-destructive'
    : ratio > 0.8
    ? 'bg-accent'
    : 'bg-primary';

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-1.5 text-muted-foreground font-mono text-[10px] uppercase tracking-widest">
          {icon}
          {label}
        </span>
        <span className="font-mono text-[11px] text-foreground tabular-nums">
          {unlimited
            ? `${formatUsed(used)} / ∞`
            : `${formatUsed(used)} / ${formatLimit(limit)}`}
        </span>
      </div>
      {!unlimited && (
        <div className="h-2 bg-muted rounded-full overflow-hidden">
          <div
            className={cn('h-full rounded-full transition-[width] duration-500 ease-out', barColor)}
            style={{ width: `${pct}%` }}
          />
        </div>
      )}
    </div>
  );
}
